import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  actionType: 'apply' | 'save' | 'share' | 'compare-salary' | 'upload-resume' | string;
  initialMode?: 'login' | 'signup';
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, darkMode, actionType, initialMode = 'login' }) => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showLegalError, setShowLegalError] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(initialMode === 'login'); // true = login, false = register
  
  // Update mode when modal opens with different initialMode
  useEffect(() => {
    if (isOpen) {
      setIsLoginMode(initialMode === 'login');
    }
  }, [isOpen, initialMode]);
  
  if (!isOpen) return null;

  const getActionText = () => {
    switch (actionType) {
      case 'apply':
        return 'apply for jobs';
      case 'save':
        return 'save jobs and companies';
      case 'share':
        return 'share opportunities';
      case 'compare-salary':
        return 'use AI-powered salary analysis';
      case 'upload-resume':
        return 'get personalized job recommendations';
      default:
        return 'use this feature';
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOAuthLogin = async (provider: 'google' | 'linkedin' | 'apple') => {
    // Check if user agreed to terms and privacy policy (only for registration)
    if (!isLoginMode && (!agreedToTerms || !agreedToPrivacy)) {
      setShowLegalError(true);
      setError('Please agree to our Terms of Service and Privacy Policy to continue.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setShowLegalError(false);
      await login(provider);
      onClose();
    } catch (err) {
      setError('Login failed. Please try again.');
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={`login-modal-backdrop ${darkMode ? 'dark' : 'light'}`} onClick={handleBackdropClick}>
      <div className="login-modal-content">
        <button className="login-modal-close" onClick={onClose}>×</button>
        
        <div className="login-modal-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
          </svg>
        </div>

        <h2 className="login-modal-title">
          {isLoginMode ? 'Welcome Back' : 'Create Your Account'}
        </h2>
        
        <p className="login-modal-message">
          {isLoginMode 
            ? `Sign in to ${getActionText()}.`
            : `Join Jobometry to ${getActionText()} and unlock powerful job market insights.`
          }
        </p>

        {!isLoginMode && (
          <div className="signup-benefits">
            <h3>How we use your data:</h3>
            <ul>
              <li>Match you with relevant job opportunities</li>
              <li>Provide personalized job recommendations</li>
              <li>Enable companies to view your profile (with your consent)</li>
              <li>Improve our services and user experience</li>
            </ul>
            <p className="privacy-commitment">
              <strong>🔒 We will never sell your personal data to third parties.</strong>
            </p>
          </div>
        )}

        {error && (
          <div className="login-modal-error">
            {error}
          </div>
        )}

        <div className="oauth-buttons">
          <button 
            className="oauth-button google-button" 
            onClick={() => handleOAuthLogin('google')}
            disabled={loading}
          >
            <svg className="oauth-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <button 
            className="oauth-button linkedin-button" 
            onClick={() => handleOAuthLogin('linkedin')}
            disabled={loading}
          >
            <svg className="oauth-icon" viewBox="0 0 24 24">
              <path fill="#0A66C2" d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
            </svg>
            {loading ? 'Signing in...' : 'Continue with LinkedIn'}
          </button>

          <button 
            className="oauth-button apple-button" 
            onClick={() => handleOAuthLogin('apple')}
            disabled={loading}
          >
            <svg className="oauth-icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {loading ? 'Signing in...' : 'Continue with Apple'}
          </button>
        </div>

        {!isLoginMode && (
          <div className="login-modal-legal">
            <div className={`legal-checkbox-group ${showLegalError && !agreedToTerms ? 'error' : ''}`}>
              <label className="legal-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked && showLegalError) {
                      setShowLegalError(false);
                      setError(null);
                    }
                  }}
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms.html" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            <div className={`legal-checkbox-group ${showLegalError && !agreedToPrivacy ? 'error' : ''}`}>
              <label className="legal-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToPrivacy}
                  onChange={(e) => {
                    setAgreedToPrivacy(e.target.checked);
                    if (e.target.checked && showLegalError) {
                      setShowLegalError(false);
                      setError(null);
                    }
                  }}
                />
                <span>
                  I have read and agree to the{' '}
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="login-modal-footer">
          <p className="login-modal-toggle">
            {isLoginMode ? (
              <>
                Don't have an account?{' '}
                <button 
                  className="toggle-mode-btn" 
                  onClick={() => setIsLoginMode(false)}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button 
                  className="toggle-mode-btn" 
                  onClick={() => setIsLoginMode(true)}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;